import { useCallback, useEffect, useState } from "react";
import { useAdminFetch } from "../lib/admin-api";
import { Button } from "../components/ui/Button";
import { Dialog, DialogTitle, DialogDescription } from "../components/ui/Dialog";
import { Input, Label } from "../components/ui/Input";
import { useToast } from "../components/ui/Toast";

/**
 * Approved-chain detail page (PR #41). Admin-only. Reuses the #/chains/:chain_id
 * hash route. Surfaces members, aliases, and recent activity, and exposes the
 * management actions (rename / add+remove location / merge / delete / aliases)
 * wired to /api/admin/chains/:chain_id/* (all admin_session cookie gated).
 */

interface MemberRow {
  license_number: string;
  license_type: string | null;
  dba_name: string | null;
  legal_name: string | null;
  display_name: string | null;
  premise_city: string | null;
  premise_street: string | null;
  premise_zip: string | null;
  status: string | null;
}

interface AliasRow {
  alias_id: string;
  alias: string;
  source: string;
}

interface AuditRow {
  audit_id: string;
  chain_id: string;
  action: string;
  payload: Record<string, unknown>;
  actor: string | null;
  created_at: string;
}

interface ChainBundle {
  chain_id: string;
  display_name: string;
  canonical_root: string;
  status: string;
  merged_into_chain_id: string | null;
  members: MemberRow[];
  aliases: AliasRow[];
  audit_log: AuditRow[];
}

interface SearchResult {
  type: "chain" | "retail_license" | "other_license";
  id: string;
  title: string;
  subtitle: string;
}

interface BridgeSummary {
  counts: { total: number; auto_attached: number; admin_attached: number };
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

/** Render a single audit row as a human-readable sentence. */
function describeAudit(a: AuditRow): string {
  const p = a.payload || {};
  const when = fmtDate(a.created_at);
  switch (a.action) {
    case "add_license":
      return `Added ${String(p.license_number ?? "location")}${p.moved_from_chain_id ? " (moved from another chain)" : ""} on ${when}`;
    case "remove_license":
      return `Removed ${String(p.license_number ?? "location")} on ${when}`;
    case "rename":
      return `Renamed from "${String(p.from_display_name ?? "")}" to "${String(p.to_display_name ?? "")}" on ${when}`;
    case "merge":
      return p.direction === "destination"
        ? `Absorbed a merged chain (${String(p.licenses_reparented ?? 0)} locations) on ${when}`
        : `Merged into another chain on ${when}`;
    case "delete":
      return `Chain deleted${p.licenses_unlinked ? ` (${String(p.licenses_unlinked)} locations detached)` : ""} on ${when}`;
    case "add_alias":
      return `Added alias "${String(p.alias ?? "")}" on ${when}`;
    case "remove_alias":
      return `Removed alias "${String(p.alias ?? "")}" on ${when}`;
    default:
      return `${a.action} on ${when}`;
  }
}

export default function ChainDetail({ chainId }: { chainId: string }) {
  const adminFetch = useAdminFetch();
  const toast = useToast();

  const [bundle, setBundle] = useState<ChainBundle | null>(null);
  const [bridge, setBridge] = useState<BridgeSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const [renameOpen, setRenameOpen] = useState(false);
  const [addLocOpen, setAddLocOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<MemberRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminFetch<ChainBundle>(`/api/admin/chains/${chainId}`);
      setBundle(r);
    } catch (err) {
      toast.push(`Failed to load chain: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [adminFetch, chainId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Distributor→parent bridge summary for the header badge. Fetched quietly:
  // it's supplementary, so a failure (e.g. a non-admin viewer) just hides the
  // badge rather than surfacing a toast.
  useEffect(() => {
    let alive = true;
    adminFetch<BridgeSummary>(`/api/admin/distributor-bridge/chain/${chainId}`)
      .then((r) => { if (alive) setBridge(r); })
      .catch(() => { if (alive) setBridge(null); });
    return () => { alive = false; };
  }, [adminFetch, chainId]);

  const removeAlias = async (alias: AliasRow) => {
    try {
      await adminFetch(`/api/admin/chains/${chainId}/aliases/${alias.alias_id}`, { method: "DELETE" });
      toast.push(`Removed alias "${alias.alias}"`, "success");
      await load();
    } catch (err) {
      toast.push(`Remove alias failed: ${(err as Error).message}`, "error");
    }
  };

  if (loading && !bundle) {
    return <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">Loading chain…</div>;
  }
  if (!bundle) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-10 text-center">
        <p className="text-gray-700 text-sm font-medium">Chain not found.</p>
        <a className="btn-primary mt-4 inline-block" href="#/chains">Back to chains</a>
      </div>
    );
  }

  const memberCount = bundle.members.length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <a href="#/chains" className="text-xs text-gray-500 hover:text-gray-800">← Chains</a>
          <h2 className="text-2xl font-semibold text-gray-900 mt-1">{bundle.display_name}</h2>
          <p className="text-sm text-gray-600 mt-1">
            <span className="font-mono text-xs">{bundle.canonical_root}</span>
            {" · "}
            <span className={bundle.status === "active" ? "text-green-700" : "text-gray-500"}>{bundle.status}</span>
            {" · "}
            {memberCount} location{memberCount === 1 ? "" : "s"}
          </p>
          {bridge && bridge.counts.total > 0 && (
            <a
              href="#/admin/distributors/bridge"
              title={`${bridge.counts.auto_attached} auto-attached · ${bridge.counts.admin_attached} admin-attached`}
              className="mt-2 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 hover:bg-green-100"
            >
              {bridge.counts.auto_attached} auto-attached distributor{bridge.counts.auto_attached === 1 ? "" : "s"}
              {bridge.counts.admin_attached > 0 && ` (+${bridge.counts.admin_attached} manual)`}
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!editing ? (
            <Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setRenameOpen(true)}>Rename</Button>
              <Button variant="secondary" onClick={() => setAddLocOpen(true)}>Add location</Button>
              <Button variant="secondary" onClick={() => setMergeOpen(true)}>Merge into…</Button>
              <Button variant="danger-ghost" onClick={() => setDeleteOpen(true)}>Delete chain</Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>Done</Button>
            </>
          )}
        </div>
      </div>

      {/* Members */}
      <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Locations ({memberCount})</h3>
        </div>
        {memberCount === 0 ? (
          <p className="px-5 py-6 text-sm text-gray-500">No locations on this chain.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {bundle.members.map((m) => (
              <li key={m.license_number} className="flex items-center justify-between gap-4 px-5 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {m.display_name || m.dba_name || m.legal_name || m.license_number}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    <span className="font-mono">{m.license_number}</span>
                    {m.premise_city ? ` · ${m.premise_city}` : ""}
                  </div>
                </div>
                {editing && (
                  <button
                    type="button"
                    aria-label={`Remove ${m.license_number}`}
                    className="shrink-0 rounded-md px-2 py-1 text-gray-400 hover:bg-red-50 hover:text-red-700"
                    onClick={() => setRemoveTarget(m)}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Aliases */}
      <AliasSection
        chainId={chainId}
        aliases={bundle.aliases}
        editable={editing}
        adminFetch={adminFetch}
        onAdded={load}
        onRemove={removeAlias}
      />

      {/* Recent activity */}
      <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Recent activity</h3>
        </div>
        {bundle.audit_log.length === 0 ? (
          <p className="px-5 py-6 text-sm text-gray-500">No activity yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {bundle.audit_log.slice(0, 10).map((a) => (
              <li key={a.audit_id} className="px-5 py-2.5 text-sm text-gray-700">
                {describeAudit(a)}
                {a.actor && <span className="ml-2 text-xs text-gray-400">{a.actor}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {renameOpen && (
        <RenameDialog
          chainId={chainId}
          current={bundle}
          adminFetch={adminFetch}
          onClose={() => setRenameOpen(false)}
          onDone={async () => { setRenameOpen(false); await load(); }}
        />
      )}
      {addLocOpen && (
        <AddLocationDialog
          chainId={chainId}
          adminFetch={adminFetch}
          onClose={() => setAddLocOpen(false)}
          onDone={async () => { setAddLocOpen(false); await load(); }}
        />
      )}
      {mergeOpen && (
        <MergeDialog
          chainId={chainId}
          sourceName={bundle.display_name}
          sourceCount={memberCount}
          adminFetch={adminFetch}
          onClose={() => setMergeOpen(false)}
          onDone={() => { setMergeOpen(false); window.location.hash = "#/chains"; }}
        />
      )}
      {deleteOpen && (
        <DeleteDialog
          chainId={chainId}
          name={bundle.display_name}
          memberCount={memberCount}
          adminFetch={adminFetch}
          onClose={() => setDeleteOpen(false)}
          onDone={() => { setDeleteOpen(false); window.location.hash = "#/chains"; }}
        />
      )}
      {removeTarget && (
        <RemoveLocationDialog
          chainId={chainId}
          member={removeTarget}
          isLast={memberCount <= 1}
          adminFetch={adminFetch}
          onClose={() => setRemoveTarget(null)}
          onDone={async (chainDeleted) => {
            setRemoveTarget(null);
            if (chainDeleted) { window.location.hash = "#/chains"; return; }
            await load();
          }}
        />
      )}
    </div>
  );
}

type AdminFetch = ReturnType<typeof useAdminFetch>;

function AliasSection({
  chainId, aliases, editable, adminFetch, onAdded, onRemove,
}: {
  chainId: string;
  aliases: AliasRow[];
  editable: boolean;
  adminFetch: AdminFetch;
  onAdded: () => Promise<void>;
  onRemove: (a: AliasRow) => Promise<void>;
}) {
  const toast = useToast();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const alias = value.trim();
    if (!alias) return;
    setBusy(true);
    try {
      await adminFetch(`/api/admin/chains/${chainId}/aliases`, {
        method: "POST",
        body: JSON.stringify({ alias }),
      });
      toast.push(`Added alias "${alias}"`, "success");
      setValue("");
      await onAdded();
    } catch (err) {
      toast.push(`Add alias failed: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-3">
        <h3 className="text-sm font-semibold text-gray-900">Aliases ({aliases.length})</h3>
      </div>
      <div className="px-5 py-4 space-y-3">
        {aliases.length === 0 ? (
          <p className="text-sm text-gray-500">No aliases.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {aliases.map((a) => (
              <span key={a.alias_id} className="inline-flex items-center gap-1 rounded-full bg-accent-50 text-accent-700 border border-accent-100 px-2.5 py-0.5 text-xs">
                {a.alias}
                {editable && (
                  <button
                    type="button"
                    aria-label={`Remove alias ${a.alias}`}
                    className="text-accent-700/70 hover:text-accent-700"
                    onClick={() => onRemove(a)}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
        {editable && (
          <div className="flex gap-2">
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              placeholder="Add an alias"
            />
            <Button variant="secondary" type="button" onClick={add} disabled={busy}>Add</Button>
          </div>
        )}
      </div>
    </section>
  );
}

function RenameDialog({
  chainId, current, adminFetch, onClose, onDone,
}: {
  chainId: string;
  current: ChainBundle;
  adminFetch: AdminFetch;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const toast = useToast();
  const [displayName, setDisplayName] = useState(current.display_name);
  const [canonicalRoot, setCanonicalRoot] = useState(current.canonical_root);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!displayName.trim()) { toast.push("Display name is required", "error"); return; }
    setBusy(true);
    try {
      await adminFetch(`/api/admin/chains/${chainId}/rename`, {
        method: "POST",
        body: JSON.stringify({ display_name: displayName.trim(), canonical_root: canonicalRoot.trim() || undefined }),
      });
      toast.push("Chain renamed.", "success");
      await onDone();
    } catch (err) {
      toast.push(`Rename failed: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogTitle>Rename chain</DialogTitle>
      <DialogDescription>Update the display name and optionally the canonical root.</DialogDescription>
      <div className="mt-5 space-y-4">
        <div>
          <Label htmlFor="rn-name">Display name</Label>
          <Input id="rn-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="rn-root" hint="(optional — lowercased, alphanumeric)">Canonical root</Label>
          <Input id="rn-root" value={canonicalRoot} onChange={(e) => setCanonicalRoot(e.target.value)} />
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
      </div>
    </Dialog>
  );
}

/** Lookup hook over /api/search, filtered by result type. */
function useSearch(filterType: SearchResult["type"]) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(term)}&limit=25`);
        const json = (await res.json()) as { results?: SearchResult[] };
        if (!cancelled) setResults((json.results || []).filter((r) => r.type === filterType));
      } catch {
        if (!cancelled) setResults([]);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, filterType]);
  return { q, setQ, results };
}

function AddLocationDialog({
  chainId, adminFetch, onClose, onDone,
}: {
  chainId: string;
  adminFetch: AdminFetch;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const toast = useToast();
  const { q, setQ, results } = useSearch("retail_license");
  const [selected, setSelected] = useState<SearchResult | null>(null);
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await adminFetch(`/api/admin/chains/${chainId}/add-license`, {
        method: "POST",
        body: JSON.stringify({ license_number: selected.id, force }),
      });
      toast.push(`Added ${selected.id}.`, "success");
      await onDone();
    } catch (err) {
      toast.push(`Add location failed: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogTitle>Add location</DialogTitle>
      <DialogDescription>Search for a retail license to add to this chain.</DialogDescription>
      <div className="mt-5 space-y-3">
        <Input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setSelected(null); }} placeholder="Search license # or name" />
        {!selected && results.length > 0 && (
          <ul className="max-h-56 overflow-auto rounded-md border border-gray-200 divide-y divide-gray-100">
            {results.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-gray-50"
                  onClick={() => setSelected(r)}
                >
                  <div className="text-sm font-medium text-gray-900">{r.title}</div>
                  <div className="text-xs text-gray-500">{r.subtitle} · <span className="font-mono">{r.id}</span></div>
                </button>
              </li>
            ))}
          </ul>
        )}
        {selected && (
          <div className="rounded-md border border-accent-200 bg-accent-50/40 p-3">
            <div className="text-sm font-medium text-gray-900">{selected.title}</div>
            <div className="text-xs text-gray-500 font-mono">{selected.id}</div>
            <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
              Reassign even if it's already on another chain (force)
            </label>
          </div>
        )}
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy || !selected}>{busy ? "Adding…" : "Add location"}</Button>
      </div>
    </Dialog>
  );
}

function MergeDialog({
  chainId, sourceName, sourceCount, adminFetch, onClose, onDone,
}: {
  chainId: string;
  sourceName: string;
  sourceCount: number;
  adminFetch: AdminFetch;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const { q, setQ, results } = useSearch("chain");
  const [selected, setSelected] = useState<SearchResult | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await adminFetch<{ licenses_reparented: number }>(`/api/admin/chains/${chainId}/merge`, {
        method: "POST",
        body: JSON.stringify({ into_chain_id: selected.id }),
      });
      toast.push(`Merged "${sourceName}" into "${selected.title}" — ${r.licenses_reparented} location(s) moved.`, "success");
      onDone();
    } catch (err) {
      toast.push(`Merge failed: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogTitle>Merge into another chain</DialogTitle>
      <DialogDescription>
        "{sourceName}" ({sourceCount} location{sourceCount === 1 ? "" : "s"}) will be merged into the chain you pick. Its locations and aliases move to the destination and this chain is marked merged.
      </DialogDescription>
      <div className="mt-5 space-y-3">
        <Input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setSelected(null); }} placeholder="Search destination chain" />
        {!selected && results.length > 0 && (
          <ul className="max-h-56 overflow-auto rounded-md border border-gray-200 divide-y divide-gray-100">
            {results.filter((r) => r.id !== chainId).map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-gray-50"
                  onClick={() => setSelected(r)}
                >
                  <div className="text-sm font-medium text-gray-900">{r.title}</div>
                  <div className="text-xs text-gray-500">{r.subtitle}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
        {selected && (
          <div className="rounded-md border border-accent-200 bg-accent-50/40 p-3">
            <div className="text-sm text-gray-700">
              Merge <span className="font-medium">{sourceName}</span> ({sourceCount}) → <span className="font-medium">{selected.title}</span>
            </div>
            <div className="text-xs text-gray-500">{selected.subtitle}</div>
          </div>
        )}
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy || !selected}>{busy ? "Merging…" : "Confirm merge"}</Button>
      </div>
    </Dialog>
  );
}

function DeleteDialog({
  chainId, name, memberCount, adminFetch, onClose, onDone,
}: {
  chainId: string;
  name: string;
  memberCount: number;
  adminFetch: AdminFetch;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await adminFetch(`/api/admin/chains/${chainId}/delete`, {
        method: "POST",
        body: JSON.stringify({ confirm: true, force: memberCount > 0 }),
      });
      toast.push(`Deleted "${name}".`, "success");
      onDone();
    } catch (err) {
      toast.push(`Delete failed: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogTitle>Delete chain</DialogTitle>
      <DialogDescription>
        This soft-deletes "{name}".
        {memberCount > 0
          ? ` Its ${memberCount} location${memberCount === 1 ? "" : "s"} will be detached (chain_id cleared).`
          : " It has no locations."}
        {" This cannot be undone from the UI."}
      </DialogDescription>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="danger-ghost" onClick={submit} disabled={busy}>{busy ? "Deleting…" : "Delete chain"}</Button>
      </div>
    </Dialog>
  );
}

function RemoveLocationDialog({
  chainId, member, isLast, adminFetch, onClose, onDone,
}: {
  chainId: string;
  member: MemberRow;
  isLast: boolean;
  adminFetch: AdminFetch;
  onClose: () => void;
  onDone: (chainDeleted: boolean) => Promise<void>;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await adminFetch<{ chain_deleted: boolean }>(`/api/admin/chains/${chainId}/remove-license`, {
        method: "POST",
        // When it's the last member, confirm the empty-chain removal.
        body: JSON.stringify({ license_number: member.license_number, confirm_delete_if_empty: isLast }),
      });
      toast.push(`Removed ${member.license_number}.`, "success");
      await onDone(!!r.chain_deleted);
    } catch (err) {
      toast.push(`Remove failed: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogTitle>Remove this location from chain?</DialogTitle>
      <DialogDescription>
        {member.display_name || member.dba_name || member.license_number} ({member.license_number}) will be detached from this chain.
        {isLast && " This is the last member — the chain will be left empty."}
      </DialogDescription>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="danger-ghost" onClick={submit} disabled={busy}>{busy ? "Removing…" : "Remove location"}</Button>
      </div>
    </Dialog>
  );
}
